import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import LockOutlinedIcon from '@material-ui/icons/LockOutlined'
import SwapHorizIcon from '@material-ui/icons/SwapHoriz'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import { toast } from 'react-toastify'

import {
    usePropertyPipelineConfig,
    useRenamePropertyPipelineConfig,
    useSavePropertyPipelineConfig,
} from '../LeasingSeason/Configurations/PipelineBuilder/usePipelineConfigApi'
import type { WorkbookSectionsState } from '../PipelineFlowDemo/workbookSectionDraft'
import type { ConditionalApprovalsPolicy } from './conditionalApprovals'
import type { LeasePacketMember } from './signableDocRoles'
import { FormSaveButton } from './FormSaveButton'
import { LeadsPipelineChooser } from './LeadsPipelineChooser'
import {
    ComposeTimelineState,
    LeadsPipelineTimeline,
    LeadsPipelineTimelineHandle,
} from './LeadsPipelineTimeline'
import {
    screenPlacementFromTemplateKey,
    screenPlacementLabel,
} from './leadsPipelinePlacement'
import type { ScreenPlacement } from './LeadsPipelineTimeline'
import {
    LEASING_SEASON_CONFIG_CONTENT_MAX_WIDTH,
    formatModifiedLabel,
} from './setupArtifacts'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
    },
    selectedWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100%',
        minHeight: 0,
        padding: theme.spacing(0.5, 1, 0),
        overflow: 'hidden',
        boxSizing: 'border-box',
    },
    selectedColumn: {
        width: '100%',
        maxWidth: LEASING_SEASON_CONFIG_CONTENT_MAX_WIDTH,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
    },
    selectedBar: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(1.25, 1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
        marginBottom: theme.spacing(1),
        flexShrink: 0,
    },
    selectedMain: {
        minWidth: 0,
        flex: 1,
    },
    selectedLabel: {
        fontSize: '0.625rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: theme.palette.text.secondary,
    },
    nameField: {
        maxWidth: 360,
        marginTop: theme.spacing(0.5),
    },
    meta: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(0.75),
    },
    selectedSide: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        justifyContent: 'flex-start',
        gap: theme.spacing(0.75),
        flexShrink: 0,
    },
    selectedSideRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'flex-end',
        gap: theme.spacing(0.75),
    },
    lockNote: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        maxWidth: 260,
        textAlign: 'right',
        lineHeight: 1.4,
    },
    editor: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
    },
    centered: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(3),
        height: '100%',
    },
    emptyHint: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        textAlign: 'center',
        maxWidth: 420,
        lineHeight: 1.5,
    },
}))

/**
 * Compose → Leads Pipeline: same chooser ↔ selected pattern as Setup tabs.
 */
export const LeadsPipelineComposeTab: React.FC<{
    apartmentId: number
    configId: number | null
    onConfigSelected: (configId: number, placement?: ScreenPlacement) => void
    onClearSelection: () => void
    workbookSections?: WorkbookSectionsState
    applicantDataSelected?: boolean
    conditionalApprovalsSelected?: boolean
    conditionalApprovalsPolicy?: ConditionalApprovalsPolicy
    signableDocsSelected?: boolean
    leasePacket?: LeasePacketMember[]
    guarantorPacket?: LeasePacketMember[]
    feeAmount?: string
    onDirtyChange?: (dirty: boolean) => void
    /** Other LSCs pin this pipeline — treat as read-only until copied. */
    sharedWithOtherConfigs?: number
    onCreateCopy?: () => void
    copying?: boolean
}> = ({
    apartmentId,
    configId,
    onConfigSelected,
    onClearSelection,
    workbookSections,
    applicantDataSelected = false,
    conditionalApprovalsSelected = false,
    conditionalApprovalsPolicy,
    signableDocsSelected = false,
    leasePacket,
    guarantorPacket,
    feeAmount,
    onDirtyChange,
    sharedWithOtherConfigs = 0,
    onCreateCopy,
    copying = false,
}) => {
    const classes = useStyles()
    const [choosing, setChoosing] = useState(configId == null)
    /** Scratch create placement overrides (API has no field yet). */
    const [scratchPlacementById, setScratchPlacementById] = useState<
        Record<number, ScreenPlacement>
    >({})

    const configQuery = usePropertyPipelineConfig(apartmentId, configId)
    const renameMutation = useRenamePropertyPipelineConfig(
        apartmentId,
        configId,
    )
    const savePipelineMutation = useSavePropertyPipelineConfig(
        apartmentId,
        configId,
    )
    const [nameDraft, setNameDraft] = useState('')
    const [timelineDirty, setTimelineDirty] = useState(false)
    const timelineRef = useRef<LeadsPipelineTimelineHandle>(null)
    const onDirtyChangeRef = useRef(onDirtyChange)
    onDirtyChangeRef.current = onDirtyChange

    useEffect(() => {
        setChoosing(configId == null)
        if (configId == null) {
            setTimelineDirty(false)
            onDirtyChangeRef.current?.(false)
        }
    }, [configId])

    useEffect(() => {
        if (configQuery.data?.name != null) {
            setNameDraft(configQuery.data.name)
        }
    }, [configQuery.data?.name])

    const config = configQuery.data
    const lockedBySeason = !!config && !config.is_editable
    const locked = lockedBySeason || sharedWithOtherConfigs > 0
    const initialTimeline = (config?.definition?.compose_timeline ||
        null) as Partial<ComposeTimelineState> | null

    const handleTimelineDirtyChange = useCallback((dirty: boolean) => {
        setTimelineDirty(dirty)
        onDirtyChangeRef.current?.(dirty)
    }, [])

    const handleSaveTimeline = useCallback(() => {
        if (!config || locked) return
        const timeline = timelineRef.current?.getState()
        if (!timeline) return
        savePipelineMutation.mutate(
            {
                ...config.definition,
                compose_timeline: timeline,
            },
            {
                onSuccess: () => {
                    timelineRef.current?.markSaved()
                },
                onError: () => {
                    toast.error('Could not save pipeline timeline.')
                },
            },
        )
    }, [config, locked, savePipelineMutation])

    const placement = useMemo((): ScreenPlacement => {
        if (configId != null && scratchPlacementById[configId]) {
            return scratchPlacementById[configId]
        }
        return screenPlacementFromTemplateKey(config?.created_from_template_key)
    }, [config?.created_from_template_key, configId, scratchPlacementById])

    const handleSelected = (
        nextId: number,
        scratchPlacement?: ScreenPlacement,
    ) => {
        if (scratchPlacement) {
            setScratchPlacementById((prev) => ({
                ...prev,
                [nextId]: scratchPlacement,
            }))
        }
        setChoosing(false)
        onConfigSelected(nextId, scratchPlacement)
    }

    const handleChange = () => {
        if (
            timelineDirty &&
            !window.confirm('You have unsaved changes. Discard them?')
        ) {
            return
        }
        setTimelineDirty(false)
        onDirtyChangeRef.current?.(false)
        setChoosing(true)
        onClearSelection()
    }

    const commitRename = () => {
        const next = nameDraft.trim()
        if (!next || !config || next === config.name) {
            if (config) setNameDraft(config.name)
            return
        }
        if (!config.is_editable) {
            toast.error('This configuration is locked and cannot be renamed.')
            setNameDraft(config.name)
            return
        }
        renameMutation.mutate(next, {
            onError: () => {
                toast.error('Could not rename configuration.')
                setNameDraft(config.name)
            },
        })
    }

    if (choosing || configId == null) {
        return <LeadsPipelineChooser onConfigSelected={handleSelected} />
    }

    if (configQuery.isLoading) {
        return (
            <Box className={classes.centered}>
                <CircularProgress size={28} />
            </Box>
        )
    }

    if (configQuery.isError || !config) {
        return (
            <Box className={classes.centered}>
                <Typography className={classes.emptyHint}>
                    Configuration not found for this property.
                </Typography>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleChange}
                >
                    Choose a pipeline
                </Button>
            </Box>
        )
    }

    const modified = formatModifiedLabel(config.modified_date)

    return (
        <Box className={classes.root}>
            <Box className={classes.selectedWrap}>
                <Box className={classes.selectedColumn}>
                    <Box className={classes.selectedBar}>
                        <Box className={classes.selectedMain}>
                            <Typography className={classes.selectedLabel}>
                                Selected Leads Pipeline
                            </Typography>
                            <TextField
                                className={classes.nameField}
                                size="small"
                                variant="outlined"
                                label="Pipeline name"
                                value={nameDraft}
                                disabled={locked || renameMutation.isLoading}
                                onChange={(e) => setNameDraft(e.target.value)}
                                onBlur={commitRename}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        const input = e.target as HTMLInputElement
                                        input.blur()
                                    }
                                }}
                            />
                            {lockedBySeason ? (
                                <Typography className={classes.meta}>
                                    Locked — linked to an active season
                                </Typography>
                            ) : sharedWithOtherConfigs > 0 ? (
                                <Typography className={classes.meta}>
                                    Used by another leasing season config
                                </Typography>
                            ) : modified ? (
                                <Typography className={classes.meta}>
                                    {modified}
                                </Typography>
                            ) : null}
                            <Box className={classes.chips}>
                                <Chip
                                    size="small"
                                    label="Property"
                                    variant="outlined"
                                    icon={
                                        locked ? (
                                            <LockOutlinedIcon
                                                style={{ fontSize: 14 }}
                                            />
                                        ) : undefined
                                    }
                                    color={locked ? 'primary' : 'default'}
                                />
                                <Chip
                                    size="small"
                                    label={screenPlacementLabel(placement)}
                                    variant="outlined"
                                />
                                {locked ? (
                                    <Chip
                                        size="small"
                                        label="Read-only"
                                        variant="outlined"
                                    />
                                ) : (
                                    <Chip
                                        size="small"
                                        label="Timeline ready"
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                        </Box>
                        <Box className={classes.selectedSide}>
                            <Box className={classes.selectedSideRow}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<SwapHorizIcon />}
                                    onClick={handleChange}
                                >
                                    Change
                                </Button>
                                {!locked ? (
                                    <FormSaveButton
                                        dirty={timelineDirty}
                                        saving={savePipelineMutation.isLoading}
                                        onSave={handleSaveTimeline}
                                    />
                                ) : null}
                            </Box>
                            {locked && onCreateCopy ? (
                                <>
                                    <Typography className={classes.lockNote}>
                                        This pipeline is locked while an active
                                        season uses it. Create a copy to make
                                        changes.
                                    </Typography>
                                    <Button
                                        size="small"
                                        color="primary"
                                        variant="contained"
                                        startIcon={<FileCopyOutlinedIcon />}
                                        disabled={copying}
                                        onClick={onCreateCopy}
                                    >
                                        Create a copy
                                    </Button>
                                </>
                            ) : null}
                        </Box>
                    </Box>
                    <Box className={classes.editor}>
                        <LeadsPipelineTimeline
                            ref={timelineRef}
                            placement={placement}
                            readOnly={locked}
                            workbookSections={workbookSections}
                            applicantDataSelected={applicantDataSelected}
                            conditionalApprovalsSelected={
                                conditionalApprovalsSelected
                            }
                            conditionalApprovalsPolicy={
                                conditionalApprovalsPolicy
                            }
                            signableDocsSelected={signableDocsSelected}
                            leasePacket={leasePacket}
                            guarantorPacket={guarantorPacket}
                            feeAmount={feeAmount}
                            initialTimeline={initialTimeline}
                            onDirtyChange={
                                locked ? undefined : handleTimelineDirtyChange
                            }
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}
